import { APP_INITIALIZER, ModuleWithProviders, NgModule } from '@angular/core';
import { Meta } from '@df/core/meta/meta';
import { AttributeLabelPipeService } from '@df/ui/common/attribute-label.pipe';
import {
    ICmsContentComponentValidationService,
    IValidtionEvent
} from '@icms/core/content/component/icms-content-component-validation.service';
import { ICmsContentService } from '@icms/core/content/icms-content.service';
import { NICmsContentComponent } from '@icms/core/content/interfaces/icms-content-component.interfaces';
import { Subscription } from 'rxjs';
import { pdpComponentVisibilityConditions } from './pdp-component-guard';

interface IComponentStyles extends NICmsContentComponent.IComponentStyles {
    pdpComponentVisibilityConditions: IRule[];
}

export interface IRule {
    attribute: string;
    logicOperator: 'and' | 'or';
    rule: 'one' | 'all' | 'none';
    options: number[];
}

@NgModule()
export class PDPComponentGuardModule {
    static forRoot(): ModuleWithProviders<PDPComponentGuardModule> {
        return {
            ngModule: PDPComponentGuardModule,
            providers: [
                {
                    provide: APP_INITIALIZER,
                    useFactory:
                        (
                            meta: Meta,
                            iCmsContentComponentValidationService: ICmsContentComponentValidationService,
                            attributeLabelPipeService: AttributeLabelPipeService
                        ) =>
                        () => {
                            ICmsContentService.registerComponentMetaPatch((metaData: NICmsContentComponent.IMeta) => {
                                // styles are only for non-block components
                                if (!metaData.block) {
                                    const firstStyleFieldIndex =
                                        metaData.fields.findIndex(field => field.group === NICmsContentComponent.EFieldGroup.styles) ||
                                        metaData.fields.length - 1;
                                    metaData.fields.splice(firstStyleFieldIndex, 0, pdpComponentVisibilityConditions);
                                }
                            });

                            const dataObjects = new Map<
                                NICmsContentComponent.IData,
                                {
                                    stateSubscription: Subscription;
                                }
                            >();
                            iCmsContentComponentValidationService.validation$.subscribe((event: IValidtionEvent) => {

                                const data = event.data as NICmsContentComponent.IDataWithStyles;
                                const rules = (data?.styles as IComponentStyles)?.pdpComponentVisibilityConditions;
                                const productData = meta.model?.type === 'product' ? meta.model.data : undefined;
                                if (rules?.length && productData) {
                                    let id = meta.model?.data?.id;
                                    const dataObject = dataObjects.get(data);
                                    if (!dataObject) {
                                        const stateSubscription = meta.subscribe(state => {
                                                // trigger update on model change
                                                iCmsContentComponentValidationService.getObjectHook(data, true)?.trigger$.next();
                                        });
                                        dataObjects.set(data, {
                                            stateSubscription
                                        });
                                    }

                                    // default is false
                                    let allowed = true;
                                    let ruleNumber;
                                    let ruleAttributeCode;
                                    // check rules
                                    rules.every((rule, index) => {
                                        if (index !== 0) {
                                            if (rule.logicOperator === 'or' && !allowed) {
                                                // stop checking rules if we already know the final result (we have 'or' operator and previous rule was met)
                                                return false;
                                            }
                                            if (rule.logicOperator === 'and' && allowed) {
                                                // no need to check this rule anymore, (as we have 'and' operator and previous rule was not met)
                                                return true;
                                            }
                                        }

                                        ruleNumber = index + 1;
                                        ruleAttributeCode = rule.attribute;
                                        const ruleOptions = rule.options;
                                        const productAttributeValue = productData[rule.attribute];
                                        const productOptions = Array.isArray(productAttributeValue)
                                            ? productAttributeValue
                                            : [productAttributeValue];
                                        if (rule.rule === 'all') {
                                            allowed = !ruleOptions?.every(option => productOptions.includes(option));
                                        } else {
                                            const some = ruleOptions?.some(option => productOptions.includes(option));
                                            allowed = !(rule.rule === 'one' ? some : !some);
                                        }
                                        return true;
                                    });
                                    if (!allowed) {
                                        event.error = {
                                            copy:
                                                'Not allowed for current product attribute rules. Blocked by Rule ' +
                                                ruleNumber +
                                                ' (' +
                                                attributeLabelPipeService.execute(ruleAttributeCode) +
                                                ')',
                                            level: 'error'
                                        };
                                    }
                                }
                            });
                        },
                    deps: [Meta, ICmsContentComponentValidationService, AttributeLabelPipeService],
                    multi: true
                }
            ]
        };
    }
}
